<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Button, Card } from '@drama/ui'

import { api, getToken } from '../api.js'
import ListToolbar from '../components/ListToolbar.vue'
import { useListView } from '../list-view.js'

const { t } = useI18n()
const router = useRouter()
const tasks = ref(null)
const error = ref('')
const view = useListView(tasks, {
  text: (task) => `${task.title}\n${task.spec}`,
  sorts: [{ value: 'default', label: t('board.sorts.default'), compare: () => 0 }, { value: 'deadline', label: t('board.sorts.deadline'), compare: (a, b) => (a.deadline === null ? Infinity : new Date(a.deadline)) - (b.deadline === null ? Infinity : new Date(b.deadline)) || 0 }, { value: 'reward', label: t('board.sorts.reward'), compare: (a, b) => b.reward - a.reward }],
})

async function load() {
  tasks.value = (await api('GET', '/api/voko/approvals')).tasks
}

/**
 * @param {string} id
 * @param {'approve'|'reject'} decision
 */
async function decide(id, decision) {
  error.value = ''
  try {
    await api('POST', `/api/voko/approvals/${id}`, { decision })
    await load()
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  if (getToken() === null) {
    await router.push({ path: '/login', query: { next: '/approvals' } })
    return
  }
  await load()
})
</script>

<template>
  <h1>{{ t('approvals.title') }}</h1>
  <p>{{ t('approvals.intro') }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <template v-if="tasks">
    <p v-if="tasks.length === 0">{{ t('approvals.empty') }}</p>
    <ListToolbar v-if="tasks.length > 0" :view="view" />
    <p v-if="tasks.length > 0 && view.shown.value.length === 0">{{ t('list.noMatch') }}</p>
    <Card v-for="task in view.shown.value" :key="task.id" class="voko-approval">
      <h2>{{ task.title }}</h2>
      <p>{{ t(`board.templates.${task.template}`) }} · {{ t('board.reward', { reward: task.reward }) }} · {{ task.deadline === null ? t('board.no_deadline') : t('board.deadline', { deadline: new Date(task.deadline).toLocaleString() }) }}</p>
      <p class="voko-spec">{{ task.spec }}</p>
      <div class="voko-actions">
        <Button @click="decide(task.id, 'approve')">{{ t('approvals.approve') }}</Button>
        <Button variant="ghost" @click="decide(task.id, 'reject')">{{ t('approvals.reject') }}</Button>
      </div>
    </Card>
  </template>
</template>

<style scoped>
.voko-approval {
  margin-bottom: var(--drama-space-md);
}
.voko-spec {
  white-space: pre-wrap;
}
.voko-actions {
  display: flex;
  gap: var(--drama-space-sm);
}
</style>
